import React from 'react';
import { IneligibleCategoryGridProps } from './types';
import { Radio, Skeleton } from '@mui/material';
import { styled } from '@mui/material/styles';
import { formatMoney } from 'src/utils/stringFormatting';
import { useStacklineTheme } from '@stackline/ui';
import { useAppSelector } from 'src/utils/Hooks';

const renderSkeletonRow = () => (
  <div className="row borderBottom">
    <div className="col first-col">
      <Skeleton width="80%" height={20} animation="wave" />
    </div>
    <div className="col">
      <Skeleton width="60%" height={20} animation="wave" />
    </div>
    <div className="col">
      <Skeleton width="60%" height={20} animation="wave" />
    </div>
    <div className="col">
      <Skeleton width="60%" height={20} animation="wave" />
    </div>
    <div className="col">
      <Skeleton width="60%" height={20} animation="wave" />
    </div>
  </div>
);

export enum RadioOptions {
  adPolicingSuspended = 'adPolicingSuspended',
  missingDecoration = 'missingDecoration',
  otherIneligible = 'otherIneligible',
  allIneligible = 'allIneligible'
}

export const IneligibleCategoryGrid: React.FC<IneligibleCategoryGridProps> = ({
  adPolicingSuspended,
  missingDecoration,
  otherIneligible,
  allIneligible,
  loading,
  filter,
  setFilter
}) => {
  const theme = useStacklineTheme();
  const currencySymbol = useAppSelector((state) => state?.retailer?.currencySymbol) || '$';

  const StyledRadio = styled(Radio)({
    padding: 0,
    marginRight: '8px',
    svg: {
      height: '12px',
      width: '12px'
    },
    '&, &.Mui-checked': {
      color: theme.colors.primary
    }
  });

  const StyledWrapper = styled('div')({
    display: 'flex',
    maxWidth: '100%',
    flexDirection: 'column',
    marginBottom: '80px',
    fontSize: '16px',
    '.row.first-row': {
      maxWidth: '100%',
      padding: '0px',
      paddingBottom: '16px'
    },
    '.row': {
      display: 'flex',
      width: '100%',
      paddingTop: '12px',
      paddingBottom: '12px',

      '.col.first-col': {
        flexGrow: 2,
        minWidth: '50%',
        flexShrink: 1
      },
      '.col': {
        minWidth: '12.5%',
        padding: '8px',
        textAlign: 'left',
        display: 'flex',
        alignItems: 'center'
      },
      '.title': {
        fontWeight: 700,
        fontSize: '12px',
        letterSpacing: 1.2
      }
    },
    '.row:last-child': {
      fontWeight: 700
    },
    '.borderBottom': {
      borderBottom: '1px solid #e0e0e0'
    }
  });

  const renderRow = (category: Record<string, any>, radioOption: RadioOptions, label: string) => (
    <div className="row borderBottom">
      <div className="col first-col">
        <StyledRadio checked={filter === radioOption} onClick={() => setFilter(radioOption)} />
        {label}
      </div>
      {Object.keys(category).map((key, index) => (
        <div key={key} className="col">
          {index !== 0 ? formatMoney(category[key], currencySymbol, 2) : category[key]}
        </div>
      ))}
    </div>
  );

  return (
    <StyledWrapper>
      <div className="row first-row">
        <div className="col first-col" />
        <div className="col title">PRODUCT COUNT</div>
        <div className="col title">AD SPEND</div>
        <div className="col title">AD SALES</div>
        <div className="col title">ROAS</div>
      </div>
      {loading ? (
        Array.from({ length: 4 }).map((_, idx) => <React.Fragment key={idx}>{renderSkeletonRow()}</React.Fragment>)
      ) : (
        <>
          {renderRow(adPolicingSuspended, RadioOptions.adPolicingSuspended, 'Ad Policing Ineligible')}
          {renderRow(missingDecoration, RadioOptions.missingDecoration, 'Missing Decoration Ineligible')}
          {renderRow(otherIneligible, RadioOptions.otherIneligible, 'Other Ineligible')}
          {renderRow(allIneligible, RadioOptions.allIneligible, 'All Ineligible')}
        </>
      )}
    </StyledWrapper>
  );
};
